"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Check, Sparkles } from "lucide-react";

/**
 * 3D-Bühne im Hintergrund + lebendiger, automatisch animierter Design-System-Baukasten
 * im Vordergrund (schaltet im Endlos-Loop selbstständig Tabs und Toggle um!).
 */
export default function ComponentsLibraryPreview() {
  const [active, setActive] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setActive((v) => !v), 2200);
    return () => clearInterval(id);
  }, []);

  return (
    <div
      aria-hidden="true"
      className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-2xl p-4 select-none"
      style={{ perspective: "900px" }}
    >
      <motion.div
        animate={{ scale: [1, 1.22, 1], opacity: [0.45, 0.85, 0.45] }}
        transition={{ duration: 4.5, repeat: Infinity }}
        className="pointer-events-none absolute left-1/2 top-1/2 h-40 w-56 -translate-x-1/2 -translate-y-1/2 rounded-full bg-rose-500/30 blur-3xl"
      />

      <div
        className="pointer-events-none absolute inset-x-[-20%] bottom-[-35%] h-[110%] opacity-45 dark:opacity-60"
        style={{
          transform: "rotateX(68deg)",
          maskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 65% 55% at 50% 50%, black 25%, transparent 80%)",
        }}
      >
        <motion.div
          animate={{ backgroundPosition: ["0px 0px", "0px 28px"] }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="h-full w-full bg-[linear-gradient(to_right,rgba(244,63,94,0.3)_1px,transparent_1px),linear-gradient(to_bottom,rgba(244,63,94,0.3)_1px,transparent_1px)] bg-[size:28px_28px]"
        />
      </div>

      {/* Gestochen scharfer Vordergrund */}
      <div className="relative z-10 flex flex-col items-center gap-3 antialiased">
        {/* Segmented Control */}
        <div className="flex items-center gap-1 rounded-xl border border-rose-400/50 bg-white p-1 shadow-md dark:bg-zinc-900">
          {["Buttons", "Tokens"].map((tab, idx) => {
            const isSelected = (active ? 1 : 0) === idx;
            return (
              <span
                key={tab}
                className={`rounded-lg px-3 py-1 text-[11px] font-extrabold transition-colors duration-300 ${
                  isSelected
                    ? "bg-rose-500 text-white shadow-[0_0_12px_rgba(244,63,94,0.6)]"
                    : "text-neutral-500 dark:text-neutral-400"
                }`}
              >
                {tab}
              </span>
            );
          })}
        </div>

        {/* Interactive-looking Row */}
        <div className="flex items-center gap-3">
          {/* Selbstschaltender Toggle */}
          <div className="flex items-center gap-2 rounded-2xl border border-black/10 bg-white px-3.5 py-2 shadow-lg dark:border-white/15 dark:bg-zinc-900">
            <span className="font-sans text-xs font-extrabold text-neutral-900 dark:text-white">
              Theme
            </span>
            <span
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-300 ${
                active ? "bg-emerald-500" : "bg-rose-500"
              }`}
            >
              <motion.span
                animate={{ x: active ? 18 : 3 }}
                transition={{ type: "spring", stiffness: 320, damping: 20 }}
                className="inline-block h-3.5 w-3.5 rounded-full bg-white shadow-xs"
              />
            </span>
          </div>

          {/* Glowing Action Button */}
          <motion.div
            animate={{ y: active ? -2 : 2 }}
            className="flex items-center gap-1.5 rounded-2xl bg-gradient-to-r from-rose-500 to-pink-600 px-3.5 py-2 text-xs font-black text-white shadow-[0_12px_25px_-4px_rgba(244,63,94,0.65)]"
          >
            {active ? (
              <Sparkles className="h-3.5 w-3.5" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
            <span>UI Ready</span>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
